import { useState, useEffect, useCallback } from 'react';
import {
  Phone,
  TestTube2,
  Loader2,
  Check,
  X,
  AlertCircle,
  Mic,
  Clock,
  Trash2,
  ArrowLeft, PhoneOutgoing
} from 'lucide-react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import {
  voiceService,
  type VoiceConfig,
  type CreateVoiceConfigDto,
  type TelephonyProvider,
  type ExotelSetup,
} from '@/services/voice.service';
import { useTenantContextStore } from '@/stores/tenantContextStore';
import { COUNTRY_CODES, isValidPhoneNumber, isValidE164, PHONE_INVALID_MESSAGE, E164_INVALID_MESSAGE } from '@/lib/phone';
import { ThemedSelect } from '@components/common/ThemedSelect';
import VoiceChannelOverview from './VoiceChannelOverview';
import TwilioProviderForm from './voice-providers/TwilioProviderForm';
import ExotelProviderForm from './voice-providers/ExotelProviderForm';

export interface VoiceFormData {
  provider: TelephonyProvider;
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioPhoneNumber: string;
  flexWorkspaceSid: string;
  flexWorkflowSid: string;
  flexQueueSid: string;
  /** ExoPhone number; Exotel only. */
  phoneNumber: string;
  /** Exotel API details, for fetching call recordings. */
  exotelAccountSid: string;
  exotelApiKey: string;
  /** Blank unless the admin is entering a new one; the saved token is never loaded. */
  exotelApiToken: string;
  exotelSubdomain: string;
  /** True when a token is already saved for this tenant. */
  exotelApiTokenSet: boolean;
  ttsProvider: 'twilio' | 'elevenlabs';
  ttsBackupProvider: string;
  elevenLabsApiKey: string;
  elevenLabsVoiceId: string;
  twilioVoice: string;
  greeting: string;
  language: string;
  handoffThreshold: number;
  recordCalls: boolean;
  transcribeCalls: boolean;
  afterHoursMessage: string;
  /** Ask the caller which language they want, after the greeting. */
  languageMenuEnabled: boolean;
  enabled: boolean;
  /** Agents may call customers. Exotel only: it's the one provider that can so far. */
  outboundEnabled: boolean;
  /** The number customers see. Blank uses the ExoPhone above. */
  outboundCallerId: string;
  /** ISO codes of the countries agents may call. */
  outboundCountries: string[];
}

export const defaultFormData: VoiceFormData = {
  provider: 'twilio',
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioPhoneNumber: '',
  flexWorkspaceSid: '',
  flexWorkflowSid: '',
  flexQueueSid: '',
  phoneNumber: '',
  exotelAccountSid: '',
  exotelApiKey: '',
  exotelApiToken: '',
  exotelSubdomain: 'api.exotel.com',
  exotelApiTokenSet: false,
  ttsProvider: 'twilio',
  ttsBackupProvider: '',
  elevenLabsApiKey: '',
  elevenLabsVoiceId: '',
  twilioVoice: 'Polly.Joanna',
  greeting: 'Hello, how can I help you today?',
  language: 'en-US',
  handoffThreshold: 0.6,
  recordCalls: true,
  transcribeCalls: true,
  afterHoursMessage: '',
  languageMenuEnabled: false,
  enabled: false,
  outboundEnabled: false,
  outboundCallerId: '',
  outboundCountries: [],
};

export const PROVIDERS: { value: TelephonyProvider; label: string }[] = [
  { value: 'twilio', label: 'Twilio' },
  { value: 'exotel', label: 'Exotel' },
];

const LANGUAGES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-IN', label: 'English (India)' },
  { value: 'hi-IN', label: 'Hindi' },
  { value: 'mr-IN', label: 'Marathi' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
];

/**
 * A stored config with no provider is a tenant created before Exotel existed.
 * The backend reads that as Twilio, so the UI must agree — otherwise the tab
 * would open on the wrong provider and a save could silently switch a live
 * tenant's call routing.
 */
export function resolveProvider(provider?: string | null): TelephonyProvider {
  return provider === 'exotel' ? 'exotel' : 'twilio';
}

/** True when the Exotel API details needed to fetch recordings are all there. */
export function exotelRecordingsReady(formData: VoiceFormData): boolean {
  return Boolean(
    formData.exotelAccountSid.trim() &&
      formData.exotelApiKey.trim() &&
      (formData.exotelApiToken.trim() || formData.exotelApiTokenSet),
  );
}

/**
 * Which fields must be filled before the configuration can be saved.
 *
 * Mirrors the API's own rule: Twilio credentials are required for Twilio and
 * skipped for Exotel, which has none because the integration never calls
 * Exotel's API.
 *
 * Number formats are checked only when the number differs from `saved` (the
 * stored config), so a config saved before the format checks existed can still
 * be re-saved, e.g. to switch it off.
 */
export function getVoiceConfigError(
  formData: VoiceFormData,
  saved?: { phoneNumber?: string | null; twilioPhoneNumber?: string | null } | null,
): string | null {
  if (formData.provider === 'exotel' && formData.outboundEnabled && formData.outboundCountries.length === 0) {
    return 'Choose at least one country agents can call.';
  }
  if (formData.provider === 'exotel') {
    if (!formData.phoneNumber.trim()) {
      return 'Enter the ExoPhone number customers will call.';
    }
    if (formData.phoneNumber !== (saved?.phoneNumber ?? '') && !isValidPhoneNumber(formData.phoneNumber)) return `ExoPhone Number: ${PHONE_INVALID_MESSAGE}`;
    return null;
  }

  if (!formData.twilioAccountSid.trim()) return 'Enter your Twilio Account SID.';
  if (!formData.twilioAuthToken.trim()) return 'Enter your Twilio Auth Token.';
  if (!formData.twilioPhoneNumber.trim()) return 'Enter your Twilio phone number.';
  if (formData.twilioPhoneNumber !== (saved?.twilioPhoneNumber ?? '') && !isValidE164(formData.twilioPhoneNumber)) return `Twilio Phone Number: ${E164_INVALID_MESSAGE}`;
  return null;
}

/**
 * Build the API payload for the selected provider.
 *
 * Credentials belonging to the other provider are not sent: switching to
 * Exotel should not push empty strings over a tenant's stored Twilio
 * credentials, so they are left out of the request entirely.
 */
export function buildConfigDto(formData: VoiceFormData): CreateVoiceConfigDto {
  const shared = {
    provider: formData.provider,
    greeting: formData.greeting || undefined,
    language: formData.language,
    handoffThreshold: formData.handoffThreshold,
    recordCalls: formData.recordCalls,
    transcribeCalls: formData.transcribeCalls,
    afterHoursMessage: formData.afterHoursMessage || undefined,
    enabled: formData.enabled,
  };

  if (formData.provider === 'exotel') {
    return {
      ...shared,
      phoneNumber: formData.phoneNumber,
      // Only for fetching recordings. A blank token is left out so the saved
      // one stays; the others are sent so they can be cleared.
      exotelAccountSid: formData.exotelAccountSid.trim(),
      exotelApiKey: formData.exotelApiKey.trim(),
      exotelSubdomain: formData.exotelSubdomain.trim() || 'api.exotel.com',
      ...(formData.exotelApiToken.trim() && { exotelApiToken: formData.exotelApiToken.trim() }),
      // Exotel only: the question is spoken by the ai-engine's own TTS,
      // which the Twilio path does not go through. Sent on every Exotel
      // save, including when off, so turning it off actually turns it off.
      //
      // No list of languages: the caller is asked an open question and may
      // name anything the recogniser supports, so there is nothing to
      // enumerate here.
      ivrConfig: {
        languageSelection: { enabled: formData.languageMenuEnabled },
      },
      // Sent on every Exotel save, including when off, so off actually means off.
      outbound: {
        enabled: formData.outboundEnabled,
        allowedCountries: formData.outboundCountries,
        ...(formData.outboundCallerId.trim() && { callerId: formData.outboundCallerId.trim() }),
      },
    } as CreateVoiceConfigDto;
  }

  return {
    ...shared,
    twilioAccountSid: formData.twilioAccountSid,
    twilioAuthToken: formData.twilioAuthToken,
    twilioPhoneNumber: formData.twilioPhoneNumber,
    flexWorkspaceSid: formData.flexWorkspaceSid || undefined,
    flexWorkflowSid: formData.flexWorkflowSid || undefined,
    flexQueueSid: formData.flexQueueSid || undefined,
    ttsProvider: formData.ttsProvider,
    ttsBackupProvider: (formData.ttsBackupProvider as 'twilio' | 'elevenlabs') || undefined,
    elevenLabsApiKey: formData.elevenLabsApiKey || undefined,
    elevenLabsVoiceId: formData.elevenLabsVoiceId || undefined,
    twilioVoice: formData.twilioVoice,
  };
}

type Step = 'overview' | 'providers';

export default function VoiceChannelSettings() {
  const { currentTenant } = useTenantContextStore();
  const tenantId = currentTenant?.id;

  // The overview always comes first when Voice Channel is opened, whether or
  // not this tenant is already configured. Saving does not send you back to
  // it; only deleting the configuration does.
  const [step, setStep] = useState<Step>('overview');
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<VoiceFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message?: string } | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [exotelSetup, setExotelSetup] = useState<ExotelSetup | null>(null);
  const [isLoadingSetup, setIsLoadingSetup] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await voiceService.getConfig(tenantId);
      setConfig(data);
      if (data) {
        setFormData({
          ...defaultFormData,
          provider: resolveProvider(data.provider),
          twilioPhoneNumber: data.twilioPhoneNumber || '',
          phoneNumber: data.phoneNumber || '',
          exotelAccountSid: data.exotelAccountSid || '',
          exotelApiKey: data.exotelApiKey || '',
          exotelSubdomain: data.exotelSubdomain || 'api.exotel.com',
          exotelApiTokenSet: Boolean(data.exotelApiTokenSet),
          flexWorkspaceSid: data.flexWorkspaceSid || '',
          ttsProvider: data.ttsProvider,
          ttsBackupProvider: data.ttsBackupProvider || '',
          twilioVoice: 'Polly.Joanna',
          greeting: data.greeting || '',
          language: data.language,
          handoffThreshold: data.handoffThreshold,
          recordCalls: data.recordCalls,
          transcribeCalls: data.transcribeCalls,
          languageMenuEnabled: Boolean(data.ivrConfig?.languageSelection?.enabled),
          enabled: data.enabled,
          outboundEnabled: Boolean(data.outbound?.enabled),
          outboundCallerId: data.outbound?.callerId || '',
          outboundCountries: data.outbound?.allowedCountries || [],
        });
      }
    } catch {
      setError('Failed to load voice configuration');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // The applet URL is built server-side, so it is fetched rather than
  // assembled here — only the gateway knows its own public webhook host.
  useEffect(() => {
    if (formData.provider !== 'exotel' || exotelSetup || isLoadingSetup) return;
    let cancelled = false;
    setIsLoadingSetup(true);
    voiceService
      .getExotelSetup(tenantId)
      .then((setup) => {
        if (!cancelled) setExotelSetup(setup);
      })
      .catch(() => {
        if (!cancelled) setExotelSetup(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSetup(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.provider, exotelSetup, isLoadingSetup, tenantId]);

  const handleChange = (field: keyof VoiceFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }) as VoiceFormData);
    setTestResult(null);
  };

  const handleProviderChange = (provider: TelephonyProvider) => {
    if (provider === formData.provider) return;

    // Switching a live tenant re-routes real calls, so it is confirmed rather
    // than applied on a stray click.
    const savedProvider = resolveProvider(config?.provider);
    if (config?.enabled && provider !== savedProvider) {
      const confirmed = window.confirm(
        `This organization is currently taking live calls through ${
          savedProvider === 'exotel' ? 'Exotel' : 'Twilio'
        }. Switching to ${
          provider === 'exotel' ? 'Exotel' : 'Twilio'
        } will change where incoming calls are answered once you save. Continue?`,
      );
      if (!confirmed) return;
    }

    handleChange('provider', provider);
  };

  const handleSave = async () => {
    const validationError = getVoiceConfigError(formData, config);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const dto = buildConfigDto(formData);
      if (config) {
        await voiceService.updateConfig(dto, tenantId);
      } else {
        await voiceService.createConfig(dto, tenantId);
      }

      await loadConfig();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setIsTesting(true);
      setTestResult(null);
      const result = await voiceService.testConnection(tenantId);
      setTestResult({ success: result.success, message: result.error });
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete the voice configuration?')) return;
    try {
      await voiceService.deleteConfig(tenantId);
      setConfig(null);
      setFormData(defaultFormData);
      setStep('overview');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete configuration');
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (step === 'overview') {
    return (
      <VoiceChannelOverview
        isConfigured={Boolean(config)}
        isEnabled={Boolean(config?.enabled)}
        onNext={() => setStep('providers')}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Voice Channel
          </h2>
          <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
            Choose your telephony provider and configure how calls are answered.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {config && (
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                config.enabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              } dark:bg-gray-800 dark:text-gray-400`}
            >
              {config.enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
          <Button variant="outline" onClick={() => setStep('overview')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Overview
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 dark:bg-red-900/20 dark:border-red-900/50 dark:text-red-300">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Provider selection */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
          Telephony Provider
        </h3>
        <div className="grid grid-cols-2 gap-2 sm:max-w-md">
          {PROVIDERS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => handleProviderChange(p.value)}
              aria-pressed={formData.provider === p.value}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors ${
                formData.provider === p.value
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Provider-specific configuration */}
      <Card className="p-6">
        {formData.provider === 'twilio' ? (
          <TwilioProviderForm
            formData={formData}
            onChange={handleChange}
            showPasswords={showPasswords}
            onTogglePassword={togglePasswordVisibility}
          />
        ) : (
          <ExotelProviderForm
            formData={formData}
            onChange={handleChange}
            setup={exotelSetup}
            isLoadingSetup={isLoadingSetup}
          />
        )}
      </Card>

      {/* Shared settings — these apply whichever provider is selected, so they
          live outside the provider tabs rather than being duplicated in each. */}
      <Card className="p-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-1 flex items-center gap-2">
              <Mic className="h-4 w-4" />
              AI Voice Agent Settings
            </h3>
            <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
              These apply to every provider.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Greeting Message
                </label>
                <textarea
                  value={formData.greeting}
                  onChange={(e) => handleChange('greeting', e.target.value)}
                  rows={2}
                  placeholder="Hello, how can I help you today?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Language
                </label>
                <ThemedSelect
                  value={formData.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Handoff Confidence Threshold
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.handoffThreshold}
                  onChange={(e) => handleChange('handoffThreshold', parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1 dark:text-gray-400">
                  <span>Less handoffs (0)</span>
                  <span className="font-medium">{formData.handoffThreshold}</span>
                  <span>More handoffs (1)</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recording &amp; Options
            </h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.recordCalls}
                  onChange={(e) => handleChange('recordCalls', e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-700"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  {formData.provider === 'exotel' ? 'Show call recordings' : 'Record all calls'}
                  {formData.provider === 'exotel' && (
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {exotelRecordingsReady(formData)
                        ? 'Each call’s recording is fetched from Exotel after the call and played from the call history.'
                        : 'Needs the Exotel API details above. Exotel records the call; this fetches the recording so it can be played here.'}
                    </span>
                  )}
                </span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.transcribeCalls}
                  onChange={(e) => handleChange('transcribeCalls', e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-700"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Transcribe calls</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-700"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  Enable voice channel
                </span>
              </label>
            </div>
          </div>

          {formData.provider === 'exotel' && (
            <div>
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <PhoneOutgoing className="h-4 w-4" />
                Calling customers
              </h3>
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p id="outbound-enabled-label" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Enable outbound calling
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Agents can call customers from a ticket, or dial a number from the keypad on the Live Call page.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.outboundEnabled}
                    aria-labelledby="outbound-enabled-label"
                    onClick={() => handleChange('outboundEnabled', !formData.outboundEnabled)}
                    className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${
                      formData.outboundEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                        formData.outboundEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {formData.outboundEnabled && (
                  <>
                    <div>
                      <label htmlFor="outbound-caller-id" className="block text-sm font-medium mb-1">
                        Caller ID
                      </label>
                      <input
                        id="outbound-caller-id"
                        type="tel"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        value={formData.outboundCallerId}
                        onChange={(e) => handleChange('outboundCallerId', e.target.value)}
                        placeholder={formData.phoneNumber || 'Your ExoPhone'}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        The number customers see. Must be an ExoPhone on this Exotel account. Leave blank to use the ExoPhone above.
                      </p>
                    </div>
                    <fieldset>
                      <legend className="block text-sm font-medium mb-1">Countries agents can call</legend>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {COUNTRY_CODES.map((c) => (
                          <label key={c.country} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                            <input
                              type="checkbox"
                              checked={formData.outboundCountries.includes(c.country)}
                              onChange={(e) =>
                                handleChange(
                                  'outboundCountries',
                                  e.target.checked
                                    ? [...formData.outboundCountries, c.country]
                                    : formData.outboundCountries.filter((code) => code !== c.country),
                                )
                              }
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-700"
                            />
                            {c.country} {c.code}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Calls to anywhere else are refused, from tickets and from the keypad.
                      </p>
                    </fieldset>
                  </>
                )}
              </div>
            </div>
          )}

          {testResult && (
            <div
              className={`p-4 rounded-lg flex items-center gap-2 ${
                testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {testResult.success ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
              {testResult.success
                ? 'Connection successful!'
                : testResult.message || 'Connection failed'}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-2">
              {config && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {/* Test Connection checks Twilio credentials, so it is only
                  offered on the Twilio tab — Exotel has none to check. */}
              {formData.provider === 'twilio' && (
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={isTesting || !formData.twilioAccountSid}
                >
                  {isTesting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube2 className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              )}
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {config ? 'Update' : 'Save'} Configuration
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
